import clsx from 'clsx';
import { forwardRef, useCallback, useRef, type HTMLAttributes } from 'react';
import { useIntl } from 'react-intl';


import * as styles from './DetailView.sty.js';
import { useKeepMounted } from './keepMounted.js';
import { usePageContext, useRegisterPagePanel } from './page-context.js';
import type {
  ControlledPagePanelProps,
  PagePanelSizes,
  PanelName,
  UncontrolledPagePanelProps,
} from './page-types.js';
import type { PanelSize } from './panel-types.js';
import * as panelStyles from './panel.sty.js';
import { Panel } from './Panel.js';
import { useWatchBreakpoint } from './useWatchBreakpoint.js';
import { useId } from '../../core/hooks/useId.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { Drawer } from '../../overlays/drawer/Drawer.js';

/** @public */
export interface DetailViewOwnProps
  extends WithChildren,
    Omit<HTMLAttributes<HTMLDivElement>, 'onResize' | keyof StylingProps>,
    StylingProps,
    DataTestId {
  /**
   * The desired width of the sidebar.
   * Depending on the available screen width and other panel configurations,
   * the size of the panel might not match the `preferredWidth` exactly.
   * @defaultValue 15%
   */
  preferredWidth?: PanelSize;
  /**
   * The minimum width of the sidebar in pixels.
   * @defaultValue 200
   */
  minWidth?: number;
  /**
   * Screen size below which the overlay Drawer will be shown.
   * @defaultValue 640
   */
  breakpoint?: number;
  /**
   * If the sidebar's width can be resized (by drag and drop)
   * @defaultValue true
   */
  resizable?: boolean;
  /**
   * Defines if the Sidebar content will be kept in the DOM, even if the content
   * is not shown to the user.
   * @defaultValue false
   */
  keepMounted?: boolean;
  /**
   * Notifies the user if an action happened that should change the dismissed state
   * like clicking on the backdrop on mobile.
   * The returned state is only a recommendation and might differ from what the actual
   * state of the panel should be.
   */
  onDismissChange?: (state: boolean, reason: string) => void;
  /**
   * Callback triggered after resizing is finished with the new panel sizes of
   * all existing panels and the newly applied panel width (in px).
   */
  onResize?: (sizes: PagePanelSizes, width: number) => void;
}

/** @public */
export interface ControlledDetailViewProps
  extends DetailViewOwnProps,
    ControlledPagePanelProps {}

/** @public */
export interface UncontrolledDetailViewProps
  extends DetailViewOwnProps,
    UncontrolledPagePanelProps {}

/** @public */
export type DetailViewProps =
  | ControlledDetailViewProps
  | UncontrolledDetailViewProps;

/**
 * The `DetailView` component is used inside the `Page` component for displaying content in the corresponding panel.
 * @public
 */
export const DetailView = forwardRef<HTMLDivElement, DetailViewProps>(
  (props, forwardedRef) => {
    const {
      breakpoint = 1280,
      className,
      defaultDismissed,
      dismissed,
      minWidth = 320,
      onDismissChange,
      onResize,
      resizable = true,
      preferredWidth = '25%',
      style,
      children,
      keepMounted = false,
      ...restProps
    }: DetailViewProps = props;

    const panelId = useId();
    const panelName: PanelName = 'details';
    const panelRef = useRef<HTMLDivElement>(null);
    const mergedRefs = useMergeRefs([panelRef, forwardedRef]);
    const intl = useIntl();

    const page = usePageContext();

    const panel = useRegisterPagePanel(panelName, {
      ref: panelRef,
      isControlled: dismissed !== undefined,
      isDismissed: dismissed ?? props.defaultDismissed,
      minWidth,
      breakpoint,
    });

    const setIsDismissedWithReason = useCallback(
      (value: boolean, reason: string) => {
        if (value === true) {
          page.dismissPanel(panelName);
        } else {
          page.expandPanel(panelName);
        }
        onDismissChange?.(value, reason);
      },
      [onDismissChange, page, panelName],
    );

    const aboveBreakpoint = useWatchBreakpoint({
      breakpoint,
      callback: setIsDismissedWithReason,
    });

    const panelChildren = useKeepMounted({ children, keepMounted });

    if (!panel) {
      return null;
    }

    const label = intl.formatMessage({
      defaultMessage: 'Detail View',
      id: 'rR+xf1BGWvDgZVww',
    });

    const isDisplayedAsDismissed = !aboveBreakpoint || panel.isDismissed;

    return (
      <Panel
        name={panelName}
        id={panelId}
        className={clsx(
          isDisplayedAsDismissed && panelStyles.dismissedPanel,
          styles.details,
        )}
        aria-label={label}
      >
        <Panel.Content
          {...restProps}
          containerSize={page.containerSize}
          ref={mergedRefs}
          label={label}
          onResize={(width) => onResize?.(page.getPanelSizes(), width)}
          preferredWidth={preferredWidth}
          minWidth={minWidth}
          handle={resizable === true ? 'left' : 'hidden'}
          isDismissed={isDisplayedAsDismissed}
          className={clsx(styles.content, className)}
          style={style}
        >
          {panelChildren}
        </Panel.Content>

        {!aboveBreakpoint ? (
          <Drawer
            className={panelStyles.drawer}
            isDismissed={panel.isDismissed}
            onDismiss={setIsDismissedWithReason}
            placement="right"
            width="wide"
          >
            <div {...restProps} className={panelStyles.drawerContent}>
              {children}
            </div>
          </Drawer>
        ) : null}
      </Panel>
    );
  },
);
(DetailView as typeof DetailView & { displayName: string }).displayName =
  'Page.DetailView';
